import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import WalletHistoryPremium from '../components/WalletHistoryPremium';
import WalletHeaderPremium from '../components/WalletHeaderPremium';
import PaymentsPausedBanner from '../components/PaymentsPausedBanner';
import usePaymentsStatus from '../hooks/usePaymentsStatus';
import { 
  BoostSuggestionCard, 
  GrowthProgressBar, 
  HeaderUpgradeCue,
  BoostActiveBadge,
} from '../components/WalletMonetization';
import { formatCoins } from '../lib/coinFormat';
import { useSmartBack } from '../hooks/useSmartBack';
import { getPublicOrigin, shareNative } from '../utils/shareUtils';
import { 
  Copy, 
  Share2, 
  Check, 
  Users, 
  X, 
  Coins, 
  Info,
  Rocket,
  ShoppingCart,
  Crown,
  Loader2
} from 'lucide-react';
import PurchaseCoinsModal from '../components/PurchaseCoinsModal';
import { canBoostAd } from '../components/AvailabilityBadge';
import { getTransactionLabel, getTransactionSubtitle, getTransactionIcon } from '../utils/transactionLabels';
import { relativeTime } from '../utils/dateFormatter';
import './Wallet.css';

function Wallet() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const walletLocation = useLocation();
  const goBackFromWallet = useSmartBack('/');
  const { user, token, updateUser } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [boostOptions, setBoostOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralError, setReferralError] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [userAds, setUserAds] = useState([]);
  const [activeBoostHoursLeft, setActiveBoostHoursLeft] = useState(null);
  
  // New Ways to Earn state
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseTab, setPurchaseTab] = useState('packages');
  const [monthlySpend, setMonthlySpend] = useState(0);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  // iter311 — dynamic admin kill-switch (separate from the env-level
  // PAYMENTS_ENABLED above). `dynamicallyPaused` is true ONLY when the
  // admin has flipped the kill-switch on via /api/admin/settings/payments.
  const { enabled: dynamicPaymentsEnabled } = usePaymentsStatus();
  const dynamicallyPaused = paymentsEnabled && !dynamicPaymentsEnabled;
  
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';
  const coins = walletData?.balance ?? user?.coins ?? 0;

  useEffect(() => {
    fetchWalletData();
    fetchBoostOptions();
    fetchProfileStatus();
    fetchReferralInfo();
    fetchUserAds();
    fetchMonthlySpend();
  }, []);

  // iter342 — Deep-link scroll to "Earn More Coins" when the Boost
  // modal (or any other surface) sends the user here with
  // `?scroll=earn`. Waits one tick for content to render so the anchor
  // is measurable. Falls back silently if the anchor isn't there yet.
  useEffect(() => {
    const params = new URLSearchParams(walletLocation.search || '');
    const scrollTarget = params.get('scroll');
    if (scrollTarget !== 'earn') return;
    const scrollToEarn = () => {
      const el = document.getElementById('earn-more-coins');
      if (el) {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (_) {
          el.scrollIntoView();
        }
      }
    };
    // Retry a couple of times while the wallet task list mounts.
    const t1 = setTimeout(scrollToEarn, 250);
    const t2 = setTimeout(scrollToEarn, 700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [walletLocation.search]);

  const fetchWalletData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/wallet`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setWalletData(data);
        // Sync balance with auth context
        if (data.balance !== user?.coins) {
          updateUser({ coins: data.balance });
        }
      }
      // On non-ok response, keep existing walletData (stale-while-revalidate)
    } catch (error) {
      console.error('Failed to fetch wallet:', error);
      // Keep existing walletData — do NOT clear on network error
    } finally {
      setLoading(false);
    }
  };

  const fetchBoostOptions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/wallet/boost-options`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setBoostOptions(data);
      }
    } catch (error) {
      console.error('Failed to fetch boost options:', error);
    }
  };

  const fetchProfileStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/coins/profile-status`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setProfileStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch profile status:', error);
    }
  };

  /**
   * Fetch referral info with a hard 5-second timeout and a guaranteed fallback.
   *
   * Why this matters: previously a missing `/api/referral/info` route caused the
   * fetch to return 404; we set nothing; the modal stayed on "Loading…" forever.
   * Now: any failure (404, 500, network, abort) falls back to a locally-built
   * referral object using `user.referral_code` + `PUBLIC_APP_URL` so the modal
   * ALWAYS opens within 2 seconds.
   */
  const fetchReferralInfo = useCallback(async () => {
    setReferralError(null);
    setReferralLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    const buildFallback = () => {
      const code = user?.referral_code || (user?.user_id ? `WS${user.user_id.slice(-6).toUpperCase()}` : 'INVITE');
      // Always a public shareable origin (never the Capacitor localhost origin).
      const origin = getPublicOrigin() || 'https://wassla.tech';
      return {
        referral_code: code,
        referral_link: `${origin.replace(/\/$/, '')}/#/auth?ref=${code}`,
        total_referrals: 0,
        total_coins_earned: 0,
        coins_per_referral: 25,
        _fallback: true,
      };
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/referral/info`,
        { headers: { 'Authorization': `Bearer ${token}` }, signal: controller.signal }
      );
      if (response.ok) {
        const data = await response.json();
        setReferralInfo(data);
      } else {
        // 404 / 500 / 403 → log + fallback so the modal still works
        console.warn('[referral] fetch failed, status=', response.status);
        setReferralInfo(buildFallback());
        setReferralError(response.status);
      }
    } catch (error) {
      console.warn('[referral] fetch error:', error?.name || error);
      setReferralInfo(buildFallback());
      setReferralError(error?.name === 'AbortError' ? 'timeout' : 'network');
    } finally {
      clearTimeout(timeout);
      setReferralLoading(false);
    }
  }, [token, user]);

  const fetchUserAds = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ads/user/me`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        // iter269: backend returns the user's ads under `data.ads`, not
        // `data.published`/`data.active` (those keys never existed). Read
        // the real key and filter via the canonical boost-eligibility
        // helper so this page, the Account wallet card, and the boost
        // modals all agree on what counts as a "live ad available to
        // boost". Keep the active-boost detection running on the raw
        // list so already-boosted ads still surface their countdown.
        const allAds = Array.isArray(data?.ads) ? data.ads : [];
        const eligibleAds = allAds.filter(canBoostAd);
        setUserAds(eligibleAds);

        // Check for active boosts across ALL of the user's ads, not
        // just the boost-eligible ones (a boosted-then-sold ad should
        // still expose its countdown).
        const boostedAd = allAds.find(ad => ad.is_boosted && ad.boost_until);
        if (boostedAd) {
          const boostEnd = new Date(boostedAd.boost_until);
          const now = new Date();
          const hoursLeft = Math.max(0, Math.ceil((boostEnd - now) / (1000 * 60 * 60)));
          setActiveBoostHoursLeft(hoursLeft > 0 ? hoursLeft : null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user ads:', error);
    }
  };

  // Fetch advanced ways to earn data — REMOVED 2026-06-01
  // (UnifiedWaysToEarn section unified into the single EarnMoreCoinsList block
  // backed by GET /api/wallet's `tasks` field — single source of truth.)

  const fetchMonthlySpend = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/monetization/packages`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMonthlySpend(data.monthly_spend || 0);
        setPaymentsEnabled(!!data.payments_enabled);
      }
    } catch (error) {
      console.error('Failed to fetch monthly spend:', error);
    }
  };

  // Handle 50-coin milestone reached.
  // Fix 2026-06-01g: previously the toast fired on every Wallet page mount
  // because `milestoneTracked` is a per-component ref. After the user passed
  // 50 coins once, every subsequent tap on the header coin badge → /wallet
  // re-mounted Wallet, reset the ref to false, and showed the toast again.
  //
  // Correct contract:
  //   1. Hit the backend POST /api/wallet/milestone-reached (idempotent).
  //   2. Show the toast ONLY when `already_tracked === false` — i.e. this
  //      is the first time we crossed the threshold.
  //   3. The backend's milestone_events collection is the persisted flag,
  //      so refresh / relogin / re-mount cannot resurface the toast.
  const milestoneTracked = useRef(false);
  const handleMilestoneReached = useCallback(async () => {
    if (milestoneTracked.current) return;
    milestoneTracked.current = true;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wallet/milestone-reached`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ milestone_key: 'wallet_50_coins', threshold: 50 }),
      });

      if (!response.ok) return;
      const data = await response.json();

      // Honour the persisted flag — don't show the toast for someone who
      // already crossed 50 in a previous session.
      if (data.success === false || data.already_tracked === true) return;

      toast.success(
        isRTL
          ? 'أحسنت! وصلت إلى 50 عملة — يمكنك الآن استخدام جميع الأدوات المميزة'
          : 'Nice! You reached 50 coins — all premium tools are now available to you',
        { duration: 5000 }
      );
    } catch (e) {
      console.error('Milestone tracking failed:', e);
    }
  }, [token, isRTL]);

  const handleReferralClick = useCallback(() => {
    // Open the modal immediately (under 2s acceptance criterion).
    setShowReferralModal(true);
    // If we don't already have data, fetch now. The fetcher always resolves
    // within 5s (with a local fallback) so the modal never gets stuck.
    if (!referralInfo) {
      fetchReferralInfo();
    }
  }, [referralInfo, fetchReferralInfo]);

  // Surface a one-time toast when the backend failed but fallback rendered,
  // so the user knows their stats may be stale but the link still works.
  const referralErrorToastShown = useRef(false);
  useEffect(() => {
    if (showReferralModal && referralError && !referralErrorToastShown.current) {
      referralErrorToastShown.current = true;
      toast.error(
        isRTL
          ? 'تعذّر تحميل إحصائيات الإحالة — لكن يمكنك المشاركة الآن'
          : "Couldn't load referral stats — but your link is ready to share",
        { duration: 3000 }
      );
    }
    if (!showReferralModal) {
      referralErrorToastShown.current = false;
    }
  }, [showReferralModal, referralError, isRTL]);

  const copyReferralCode = async () => {
    if (referralInfo?.referral_code) {
      try {
        await navigator.clipboard.writeText(referralInfo.referral_code);
        setCopied(true);
        toast.success(t('wall.codeCopied'));
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error(t('wall.failedToCopy'));
      }
    }
  };

  const copyReferralLink = async () => {
    if (referralInfo?.referral_link) {
      try {
        await navigator.clipboard.writeText(referralInfo.referral_link);
        toast.success(t('wall.linkCopied'));
      } catch (err) {
        toast.error(t('wall.failedToCopy'));
      }
    }
  };

  const shareReferral = async () => {
    if (!referralInfo) return;
    const res = await shareNative({
      title: t('wall.joinWassla'),
      text: isRTL
        ? `سجل في وصلة واحصل على عملات مجانية! استخدم رمز الإحالة: ${referralInfo.referral_code}`
        : `Sign up on Wassla and get free coins! Use my referral code: ${referralInfo.referral_code}`,
      url: referralInfo.referral_link,
    });
    if (!res.completed) {
      copyReferralLink();
    }
  };

  // Generate trend data for sparkline (mock - could be from actual transaction history)
  const trendData = useMemo(() => {
    // Generate sparkline from recent transactions or use default
    if (walletData?.transactions?.length > 0) {
      return walletData.transactions.slice(0, 7).map(t => Math.abs(t.amount || 1));
    }
    return [2, 4, 3, 6, 5, 8, coins || 7];
  }, [walletData, coins]);

  return (
    <div className="min-h-screen bg-[#f6f7f8]" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Premium Wallet Header */}
      <WalletHeaderPremium
        balance={coins}
        currencyLabel={t('coins')}
        trend={trendData}
        dir={isRTL ? 'rtl' : 'ltr'}
        lang={lang}
        onBack={goBackFromWallet}
        onHistory={() => {
          document.getElementById('wallet-history-section')?.scrollIntoView({ behavior: 'smooth' });
        }}
        subtext={<HeaderUpgradeCue lang={lang} coins={coins} />}
      />

      {/* Content with safe bottom padding */}
      <div className="max-w-[560px] mx-auto px-4 pt-4 pb-28">
        
        {/* ========================================
            YOUR COINS DESCRIPTION
            ======================================== */}
        <div className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm mb-4" data-testid="wallet-coins-desc">
          <h2 className="text-lg font-bold text-slate-800 mb-1">
            {isRTL ? 'عملاتك' : 'Your Coins'}
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            {isRTL
              ? 'استخدم العملات لفتح المزايا المميزة مثل السيرة الذاتية والإعلانات المدفوعة.'
              : 'Use coins to unlock premium features like CV templates and ad boosts.'}
          </p>
        </div>

        {/* ========================================
            EARN MORE COINS — interactive task list
            ======================================== */}
        <EarnMoreCoinsList
          isRTL={isRTL}
          tasks={walletData?.tasks || []}
          token={token}
          onChanged={(newBalance) => {
            fetchWalletData();
            if (typeof newBalance === 'number') updateUser({ coins: newBalance });
          }}
          onInvite={handleReferralClick}
          navigate={navigate}
        />

        {/* ========================================
            PRIMARY ACTION: BOOST MY AD BUTTON
            ======================================== */}
        <div className="mb-4">
          <button 
            onClick={() => navigate('/my-ads', { state: { openBoost: true } })}
            className="w-[90%] mx-auto py-4 rounded-2xl text-white font-bold text-base transition-all flex items-center justify-center gap-2"
            style={{ height: 56, background: 'linear-gradient(135deg, #1F6F3A, #1F6F3A)', boxShadow: '0 8px 24px rgba(22,163,74,0.35)' }}
            data-testid="boost-my-ad-btn"
          >
            <Rocket size={20} />
            {t('wall.boostMyAd')}
          </button>
          <p className="text-center text-xs text-slate-500 mt-2">
            {userAds.length > 0
              ? (t('wall.increaseVisibilityInstantly'))
              : (t('wall.postAnAdToStart'))}
          </p>
          
          {/* Micro-urgency: Active Boost Badge */}
          <BoostActiveBadge lang={lang} hoursLeft={activeBoostHoursLeft} />

          {/* iter341 — Paid coin / subscription entry points REMOVED
              per operator directive. Wassla intentionally keeps coins
              as internal earned rewards only; no Buy Coins, Upgrade
              Pro, Bankak/Visa payment, "coming soon" pill, or
              contact-support-to-activate messaging. Boost still uses
              earned coins via the BoostSuggestionCard below.
              PurchaseCoinsModal + backend payment routes are LEFT
              INTACT (feature-flag gated) so a future re-enable
              doesn't require re-implementation.

              Replacement wording is the operator-approved copy:
              EN: "Earn coins by using Wassla. Complete tasks to
                   collect coins and boost your ads."
              AR: "اكسب العملات من خلال استخدام وصلة. أكمل المهام
                   واجمع العملات لترقية إعلاناتك."
          */}
          <div
            className="w-[90%] mx-auto mt-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-center"
            data-testid="wallet-earn-only-note"
          >
            <div className="text-sm font-semibold text-emerald-800">
              {isRTL
                ? 'اكسب العملات من خلال استخدام وصلة.'
                : 'Earn coins by using Wassla.'}
            </div>
            <div className="text-[12px] text-emerald-700/90 mt-1 leading-snug">
              {isRTL
                ? 'أكمل المهام واجمع العملات لترقية إعلاناتك.'
                : 'Complete tasks to collect coins and boost your ads.'}
            </div>
          </div>
        </div>

        {/* ========================================
            GROWTH PROGRESS BAR
            ======================================== */}
        <div className="bg-white rounded-2xl border border-black/5 p-4 shadow-sm mb-4">
          <GrowthProgressBar
            lang={lang}
            currentCoins={coins}
            targetCoins={50}
            onMilestoneReached={handleMilestoneReached}
          />
        </div>

        {/* ========================================
            BOOST SUGGESTION CARD (if applicable)
            ======================================== */}
        {userAds.length > 0 && (
          <div className="mb-4">
            <BoostSuggestionCard
              lang={lang}
              coins={coins}
              hasPublishedAds={userAds.length > 0}
              adTitle={userAds[0]?.title}
              views7d={userAds[0]?.views || 0}
              ctr={0.015}
              messages7d={userAds[0]?.inquiries || 0}
              onBoostClick={() => navigate('/wallet/boost/choose-ad', { state: { from: '/wallet' } })}
            />
          </div>
        )}

        {/* ========================================
            UNIFIED WAYS TO EARN — REMOVED 2026-06-01
            The single source of truth is now <EarnMoreCoinsList /> above,
            backed by GET /api/wallet's `tasks` field. We no longer render the
            legacy /api/wallet/ways-to-earn block to avoid duplicate earning
            sections (Cashback / Volume / Sharing / Fast Responder are not
            fully wired end-to-end yet).
            ======================================== */}

        {/* Transaction History Section */}
        <div className="rounded-3xl overflow-hidden shadow-sm border border-black/5 bg-white">
          <div className="p-5" id="wallet-history-section">
            {loading && !walletData ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <Loader2 size={24} className="animate-spin" style={{ color: '#9CA3AF', margin: '0 auto 8px' }} />
                <p style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 600 }}>
                  {isRTL ? 'جارِ التحميل...' : 'Loading...'}
                </p>
              </div>
            ) : (
              <WalletHistoryPremium
                items={
                  walletData?.recent_transactions?.slice(0, 5).map((txn, idx) => ({
                    id: txn.transaction_id || txn.tx_id || `txn-${idx}`,
                    type: txn.direction === '+' || txn.type?.startsWith('earn_') ? 'credit' : 'debit',
                    title: getTransactionLabel(txn.type || txn.reason_code || txn.reason, lang),
                    subtitle: getTransactionSubtitle(txn, lang),
                    amount: (txn.direction === '+' || txn.type?.startsWith('earn_'))
                      ? Math.abs(txn.amount)
                      : -Math.abs(txn.amount),
                    currencyLabel: isRTL ? 'عملة' : 'coins',
                    createdAt: txn.created_at,
                    meta: { 
                      icon: getTransactionIcon(txn)
                    }
                  })) || []
                }
                locale={lang}
                dir={isRTL ? 'rtl' : 'ltr'}
                onViewAll={walletData?.recent_transactions?.length > 5 
                  ? () => navigate('/wallet/history') 
                  : undefined
                }
                viewAllLabel={t('viewAllHistory', 'View All')}
                emptyLabel={t('noTransactions', 'No transactions yet')}
              />
            )}
          </div>
        </div>

        {/* Rules Section - Updated for closed-loop economy */}
        <div className="mt-4 rounded-2xl border border-black/10 bg-white p-4">
          <div className="flex items-center gap-2 mb-2">
            <Info size={16} className="text-slate-500" />
            <h3 className="font-semibold text-slate-900">{t('wall.coinRules')}</h3>
          </div>
          <ul className="space-y-1 text-sm text-slate-600">
            <li>• {t('wall.coinsCanBeUsedFor')}</li>
            <li>• {t('wall.dailyLoginStreaksIncreaseBonuses')}</li>
            <li>• {t('wall.coinsAreNotTransferableAnd')}</li>
            <li>• {t('wall.earnedCoinsExpireAfter60')}</li>
          </ul>
        </div>

        <div className="h-4" />
      </div>

      {/* Referral Modal */}
      {showReferralModal && (
        <ReferralModal 
          isRTL={isRTL}
          referralInfo={referralInfo}
          loading={referralLoading && !referralInfo}
          onClose={() => setShowReferralModal(false)}
          onCopyCode={copyReferralCode}
          onCopyLink={copyReferralLink}
          onShare={shareReferral}
          copied={copied}
        />
      )}

      {/* iter341 — Purchase Coins Modal REMOVED from Wallet surface.
          The component still exists at /components/PurchaseCoinsModal
          (backend routes intact) so a future admin can re-enable
          purchases via a feature flag without a rewrite. */}
    </div>
  );
}

// Referral Modal Component
function ReferralModal({ isRTL, referralInfo, loading, onClose, onCopyCode, onCopyLink, onShare, copied }) {
  const { t } = useTranslation();

  // Only show the Loading… state for the brief window before the fetcher
  // settles (≤5s). After that, `referralInfo` is guaranteed to be set —
  // either from the API or from the local fallback in fetchReferralInfo().
  if (loading || !referralInfo) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="referral-modal-loading"
      >
        <div className="bg-white rounded-3xl w-full max-w-[400px] overflow-hidden shadow-xl p-8 text-center" onClick={(e) => e.stopPropagation()}>
          <Loader2 size={24} className="animate-spin mx-auto text-slate-400 mb-3" />
          <p className="text-sm text-slate-500">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
          <button
            className="mt-4 text-xs text-slate-400 underline"
            onClick={onClose}
            data-testid="referral-loading-close"
          >
            {isRTL ? 'إغلاق' : 'Close'}
          </button>
        </div>
      </div>
    );
  }

  // Reward value comes from the backend (single source of truth); falls back to 25.
  const rewardCoins = referralInfo.coins_per_referral ?? 25;

  const howItWorks = isRTL
    ? [
        { step: '1', text: 'شارك رابط الدعوة أو كود الإحالة' },
        { step: '2', text: 'يسجل صديقك باستخدامه' },
        { step: '3', text: `عندما ينشر أول إعلان له بنجاح، تُضاف لك ${rewardCoins} عملة تلقائيًا` },
      ]
    : [
        { step: '1', text: 'Share your referral code or link' },
        { step: '2', text: 'Your friend signs up using it' },
        { step: '3', text: `When they publish their first ad, you earn ${rewardCoins} coins automatically` },
      ];

  const rules = isRTL
    ? [
        'المكافأة تُحسب فقط عند نشر أول إعلان',
        'المسودات لا تُحتسب',
        'تُمنح المكافأة مرة واحدة لكل صديق',
        'يجب ربط الإحالة أثناء التسجيل',
      ]
    : [
        'Reward applies only to the first published ad',
        'Drafts do not count',
        'Reward is credited once per invited friend',
        'Referral must be linked at signup',
      ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="referral-modal">
      {/* iter294 — Mobile scroll fix.
          - `max-h-[85dvh]` uses dynamic viewport height so iOS Safari's
            URL bar / Android keyboards don't clip the share button or
            the terms section under the bottom nav.
          - `pb-[calc(env(safe-area-inset-bottom)+88px)]` reserves room
            for the orange + button + BottomNav on mobile, so the
            terms list is always reachable above them.
          - The close (X) header is sticky so it stays tappable when
            the user scrolls down to the rules section. */}
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-[400px] max-h-[85dvh] sm:max-h-[90dvh] overflow-y-auto shadow-xl overscroll-contain"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' }}
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
        data-testid="referral-modal-scroll"
      >
        {/* Header — sticky so the close button is always reachable. */}
        <div className="sticky top-0 z-10 p-6 bg-gradient-to-br from-pink-500 to-purple-600 text-white text-center rounded-t-3xl">
          <button 
            className="absolute top-4 end-4 h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center transition-colors"
            onClick={onClose}
            data-testid="referral-close"
            aria-label={isRTL ? 'إغلاق' : 'Close'}
          >
            <X size={20} />
          </button>
          <div className="h-14 w-14 mx-auto rounded-2xl bg-white/20 border border-white/30 grid place-items-center mb-3">
            <Users size={28} />
          </div>
          <h2 className="text-lg font-bold">{isRTL ? 'ادعُ أصدقاءك' : 'Invite Friends'}</h2>
          <p className="text-white/90 text-sm mt-1 leading-relaxed px-2">
            {isRTL
              ? `اربح ${rewardCoins} عملة عندما يسجل صديقك عبر رابطك وينشر أول إعلان له بنجاح.`
              : `Earn ${rewardCoins} coins when your friend signs up with your link and publishes their first ad.`}
          </p>
        </div>

        <div className="p-5 space-y-4">
          {/* How it works */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
              {isRTL ? 'كيف تعمل' : 'How it works'}
            </h4>
            <div className="space-y-2">
              {howItWorks.map((item) => (
                <div key={item.step} className="flex items-start gap-2.5">
                  <span className="h-5 w-5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold flex-shrink-0 grid place-items-center mt-0.5">
                    {item.step}
                  </span>
                  <span className="text-sm text-slate-700 leading-snug">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Referral Code */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              {isRTL ? 'كود الإحالة' : 'Your referral code'}
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex-1 px-4 py-3 bg-slate-100 rounded-xl font-mono text-lg font-bold text-slate-800 text-center" data-testid="referral-code">
                {referralInfo.referral_code}
              </div>
              <button 
                className="h-12 w-12 rounded-xl bg-slate-100 hover:bg-slate-200 grid place-items-center transition-colors"
                onClick={onCopyCode}
                data-testid="referral-copy-code"
              >
                {copied ? <Check size={20} className="text-green-600" /> : <Copy size={20} className="text-slate-600" />}
              </button>
            </div>
          </div>

          {/* Referral Link */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              {isRTL ? 'أو شارك الرابط' : 'Or share the link'}
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <input 
                type="text" 
                value={referralInfo.referral_link || ''} 
                readOnly 
                className="flex-1 px-3 py-2.5 bg-slate-100 rounded-xl text-sm text-slate-600 truncate"
                data-testid="referral-link"
              />
              <button 
                className="h-10 w-10 rounded-xl bg-slate-100 hover:bg-slate-200 grid place-items-center transition-colors"
                onClick={onCopyLink}
                data-testid="referral-copy-link"
              >
                <Copy size={18} className="text-slate-600" />
              </button>
            </div>
          </div>

          {/* Share Button */}
          <button 
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-500/25"
            onClick={onShare}
            data-testid="referral-share-btn"
          >
            <Share2 size={18} />
            {isRTL ? 'مشاركة' : 'Share'}
          </button>

          {/* Stats */}
          {(referralInfo.total_referrals > 0 || referralInfo.total_coins_earned > 0) && (
            <div className="flex gap-3 pt-1">
              <div className="flex-1 text-center p-3 rounded-xl bg-green-50 border border-green-200">
                <div className="text-xl font-bold text-green-700">{referralInfo.total_referrals || 0}</div>
                <div className="text-[10px] text-green-600 mt-0.5">
                  {isRTL ? 'أصدقاء مدعوين' : 'Friends invited'}
                </div>
              </div>
              <div className="flex-1 text-center p-3 rounded-xl bg-yellow-50 border border-yellow-200">
                <div className="text-xl font-bold text-yellow-700">+{referralInfo.total_coins_earned || 0}</div>
                <div className="text-[10px] text-yellow-600 mt-0.5">
                  {isRTL ? 'عملات مكتسبة' : 'Coins earned'}
                </div>
              </div>
            </div>
          )}

          {/* Rules */}
          <div className="pt-1 border-t border-slate-100">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
              {isRTL ? 'الشروط' : 'Rules'}
            </p>
            <ul className="space-y-1">
              {rules.map((rule, i) => (
                <li key={i} className="text-[11px] text-slate-500 leading-snug flex items-start gap-1.5">
                  <span className="text-slate-300 mt-0.5">•</span>
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Wallet;

// =====================================================
// EARN MORE COINS — interactive task list
// =====================================================

function EarnMoreCoinsList({ isRTL, tasks, token, onChanged, onInvite, navigate }) {
  const [claiming, setClaiming] = useState(null);
  // iter305 — Seller Story submission modal state
  const [storyOpen, setStoryOpen] = useState(false);
  const [storyTitle, setStoryTitle] = useState('');
  const [storyBody, setStoryBody] = useState('');
  const [storySubmitting, setStorySubmitting] = useState(false);

  // Hide tasks we don't want to surface in this block (PHONE_VERIFIED is shown elsewhere)
  // iter305 — added SELLER_STORY_APPROVED
  const ORDER = ['PROFILE_COMPLETE', 'FIRST_AD_POST', 'DAILY_CHECKIN', 'WATCH_AD', 'REFERRAL_AFTER_AD_POST', 'SELLER_STORY_APPROVED'];
  const byCode = Object.fromEntries((tasks || []).map((t) => [t.task_code, t]));
  const ordered = ORDER.map((c) => byCode[c]).filter(Boolean);

  const META = {
    PROFILE_COMPLETE: {
      icon: '👤',
      en: 'Complete your profile',
      ar: 'إكمال الملف الشخصي',
      lockedEn: 'Add name, city and avatar to claim',
      lockedAr: 'أضف الاسم والمدينة والصورة لاستلام المكافأة',
      // iter340 — Deep-link into Account edit mode. The Wallet was
      // previously sending `?edit=1` but Account.js checks
      // `searchParams.get('edit') === 'true'` (see the shouldEdit
      // guard at line ~219), so the auto-edit-mode effect NEVER
      // fired and the seller had to hunt for the pencil icon. The
      // `from=coins` flag also unlocks the operator-approved info
      // toast reminding the seller what to fill (name/address/photo).
      onAction: () => navigate('/account?edit=true&from=coins'),
    },
    FIRST_AD_POST: {
      icon: '📣',
      en: 'First approved ad',
      ar: 'أول إعلان موافق عليه',
      lockedEn: 'Publish your first ad — auto-credit on approval',
      lockedAr: 'انشر أول إعلان — يُضاف تلقائيًا بعد الموافقة',
      onAction: () => navigate('/add-ad?from=wallet'),
    },
    DAILY_CHECKIN: {
      icon: '📅',
      en: 'Daily login',
      ar: 'تسجيل الدخول اليومي',
      lockedEn: 'Come back tomorrow',
      lockedAr: 'ارجع غدًا',
    },
    WATCH_AD: {
      icon: '🎬',
      en: 'Watch ads',
      ar: 'مشاهدة الإعلانات',
      lockedEn: 'Coming soon',
      lockedAr: 'قريباً',
    },
    REFERRAL_AFTER_AD_POST: {
      icon: '🤝',
      en: 'Invite a friend',
      ar: 'ادعُ صديقًا',
      lockedEn: 'Earn after they verify or post their first ad',
      lockedAr: 'تحصل عليها بعد تحقّق صديقك أو نشر أول إعلان',
      onAction: onInvite,
    },
    // iter305 — Seller success story (admin-approved, +10).
    SELLER_STORY_APPROVED: {
      icon: '🏆',
      en: 'Share your success story',
      ar: 'شارك قصة نجاحك',
      lockedEn: 'Tell us how Wassla helped you — admin approval required',
      lockedAr: 'احكِ لنا كيف ساعدتك وصلة — تتطلب موافقة المسؤول',
      onAction: () => setStoryOpen(true),
    },
  };

  const claim = async (taskCode) => {
    setClaiming(taskCode);
    try {
      const url =
        taskCode === 'DAILY_CHECKIN'
          ? `${process.env.REACT_APP_BACKEND_URL}/api/wallet/checkin`
          : `${process.env.REACT_APP_BACKEND_URL}/api/wallet/claim/${taskCode}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(
          isRTL
            ? `+${data.amount} عملة • رصيدك: ${data.new_balance}`
            : `+${data.amount} coin${data.amount === 1 ? '' : 's'} · Balance: ${data.new_balance}`
        );
        onChanged?.(data.new_balance);
      } else {
        toast.error(
          (typeof data.detail === 'string' && data.detail) ||
            (isRTL ? 'تعذر استلام المكافأة. حاول لاحقًا.' : 'Could not claim reward. Try again.')
        );
      }
    } catch {
      toast.error(isRTL ? 'حدث خطأ في الشبكة.' : 'Network error.');
    } finally {
      setClaiming(null);
    }
  };

  // iter305 — Seller story submission
  const submitStory = async () => {
    const title = (storyTitle || '').trim();
    const body = (storyBody || '').trim();
    if (title.length < 4) {
      toast.error(isRTL ? 'العنوان قصير جدًا (4 أحرف على الأقل)' : 'Title too short (min 4 chars)');
      return;
    }
    if (body.length < 30) {
      toast.error(isRTL ? 'القصة قصيرة جدًا (30 حرفًا على الأقل)' : 'Story too short (min 30 chars)');
      return;
    }
    setStorySubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seller-stories`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title, body }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(
          isRTL
            ? 'تم إرسال قصتك للمراجعة. سنخبرك عند الموافقة!'
            : 'Story submitted! We will let you know when admin approves it.'
        );
        setStoryOpen(false);
        setStoryTitle('');
        setStoryBody('');
        onChanged?.(); // refresh wallet (no new balance yet)
      } else {
        toast.error(
          (typeof data.detail === 'string' && data.detail) ||
            (isRTL ? 'تعذر إرسال القصة. حاول لاحقًا.' : 'Could not submit story. Try again.')
        );
      }
    } catch {
      toast.error(isRTL ? 'حدث خطأ في الشبكة.' : 'Network error.');
    } finally {
      setStorySubmitting(false);
    }
  };

  const renderRow = (task) => {
    const meta = META[task.task_code];
    if (!meta) return null;

    const status = task.status; // claimable | claimed | locked | coming_soon | submitted
    const isClaimable = status === 'claimable';
    const isComingSoon = status === 'coming_soon';
    const isSubmitted = status === 'submitted';

    // Locked sub-label for context
    let sub = null;
    if (status === 'locked') sub = isRTL ? meta.lockedAr : meta.lockedEn;
    else if (isComingSoon) sub = isRTL ? meta.lockedAr : meta.lockedEn;
    else if (status === 'claimed' && task.recurring === 'daily')
      sub = isRTL ? 'تم اليوم — ارجع غدًا' : 'Done today — come back tomorrow';
    else if (status === 'claimed') sub = isRTL ? 'تم الاستلام' : 'Claimed';
    else if (isSubmitted)
      sub = isRTL ? 'في انتظار موافقة المسؤول' : 'Waiting for admin approval';
    else if (task.task_code === 'WATCH_AD' && task.count_today !== undefined)
      sub = isRTL
        ? `${task.count_today}/${task.daily_cap || 3} اليوم`
        : `${task.count_today}/${task.daily_cap || 3} today`;
    else if (task.task_code === 'REFERRAL_AFTER_AD_POST' && task.completed_count > 0)
      sub = isRTL
        ? `${task.completed_count} إحالة ناجحة`
        : `${task.completed_count} successful referral${task.completed_count === 1 ? '' : 's'}`;
    else if (task.task_code === 'FIRST_AD_POST' && isClaimable)
      sub = isRTL
        ? 'سيتم إضافتها تلقائيًا عند الموافقة'
        : 'Auto-credited on approval';

    // CTA label
    let ctaLabel = isRTL ? 'استلم' : 'Claim';
    if (status === 'claimed') ctaLabel = isRTL ? 'تم' : 'Claimed';
    if (status === 'locked') ctaLabel = isRTL ? 'ابدأ' : 'Start';
    if (isComingSoon) ctaLabel = isRTL ? 'قريباً' : 'Coming soon';
    if (isSubmitted) ctaLabel = isRTL ? 'قيد المراجعة' : 'Pending';
    if (task.task_code === 'REFERRAL_AFTER_AD_POST') ctaLabel = isRTL ? 'دعوة' : 'Invite';
    if (task.task_code === 'SELLER_STORY_APPROVED' && isClaimable)
      ctaLabel = isRTL ? 'إرسال قصة' : 'Submit story';
    if (task.task_code === 'FIRST_AD_POST' && isClaimable)
      ctaLabel = isRTL ? 'انتظر الموافقة' : 'Auto';

    const onClick = () => {
      // iter305 — FIRST_AD_POST auto-credits on ad approval; tapping
      // the button just informs the user. SELLER_STORY uses the
      // submission modal. REFERRAL uses the invite sheet.
      if (task.task_code === 'FIRST_AD_POST' && isClaimable) {
        return toast.info(isRTL
          ? 'سيتم إضافة العملات تلقائيًا بعد الموافقة على إعلانك'
          : 'Coins will be auto-credited once your ad is approved');
      }
      if (task.task_code === 'SELLER_STORY_APPROVED' && isClaimable) {
        return setStoryOpen(true);
      }
      if (isClaimable && task.task_code !== 'REFERRAL_AFTER_AD_POST') return claim(task.task_code);
      if (status === 'locked' && meta.onAction) return meta.onAction();
      if (task.task_code === 'REFERRAL_AFTER_AD_POST' && onInvite) return onInvite();
    };

    const disabled =
      isComingSoon ||
      status === 'claimed' ||
      isSubmitted ||
      claiming === task.task_code ||
      (status === 'locked' && !meta.onAction);

    return (
      <li
        key={task.task_code}
        className="flex items-center gap-3 py-2.5"
        data-testid={`earn-task-${task.task_code}`}
      >
        <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center text-lg flex-shrink-0">
          {meta.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
            <span>{isRTL ? meta.ar : meta.en}</span>
            <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
              +{task.reward}{' '}
              {isRTL ? (task.reward === 1 ? 'عملة' : 'عملات') : `coin${task.reward === 1 ? '' : 's'}`}
              {task.task_code === 'WATCH_AD'
                ? isRTL
                  ? ` · حتى ${task.daily_cap || 3}/يوم`
                  : ` · up to ${task.daily_cap || 3}/day`
                : ''}
            </span>
          </div>
          {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
        </div>
        <button
          onClick={onClick}
          disabled={disabled}
          data-testid={`earn-task-${task.task_code}-btn`}
          className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex-shrink-0 ${
            disabled
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-emerald-600 hover:bg-emerald-700 text-white active:scale-[0.98]'
          }`}
        >
          {claiming === task.task_code ? '…' : ctaLabel}
        </button>
      </li>
    );
  };

  return (
    <div
      id="earn-more-coins"
      className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm mb-4"
      data-testid="wallet-earn-coins"
      style={{ scrollMarginTop: 80 }}
    >
      <h3 className="text-base font-bold text-slate-800 mb-2">
        {isRTL ? 'احصل على المزيد من العملات' : 'Earn More Coins'}
      </h3>
      <ul className="divide-y divide-slate-100">{ordered.map(renderRow)}</ul>

      {/* iter305 — Seller Story submission modal. Tap "Submit story" on
          the SELLER_STORY_APPROVED row to open. Admin reviews + approves
          → +10 coins credited to the seller's wallet. */}
      {storyOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
          data-testid="seller-story-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !storySubmitting) setStoryOpen(false); }}
        >
          <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-md max-h-[90vh] overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 grid place-items-center text-xl">🏆</div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-slate-800">
                  {isRTL ? 'شارك قصة نجاحك' : 'Share your success story'}
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isRTL
                    ? 'بعد موافقة المسؤول ستحصل على +10 عملات.'
                    : 'After admin approval you will receive +10 coins.'}
                </p>
              </div>
            </div>

            <label className="block text-xs font-semibold text-slate-600 mb-1">
              {isRTL ? 'العنوان' : 'Title'}
            </label>
            <input
              type="text"
              data-testid="seller-story-title"
              value={storyTitle}
              onChange={(e) => setStoryTitle(e.target.value)}
              maxLength={120}
              placeholder={isRTL ? 'كيف ساعدتك وصلة...' : 'How Wassla helped me…'}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />

            <label className="block text-xs font-semibold text-slate-600 mb-1">
              {isRTL ? 'قصتك' : 'Your story'}
            </label>
            <textarea
              data-testid="seller-story-body"
              value={storyBody}
              onChange={(e) => setStoryBody(e.target.value)}
              maxLength={4000}
              rows={6}
              placeholder={
                isRTL
                  ? 'احكِ لنا تجربتك في البيع على وصلة (30 حرفًا على الأقل)...'
                  : 'Tell us about your selling experience on Wassla (min 30 characters)...'
              }
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />
            <div className="text-xs text-slate-400 mb-3">
              {storyBody.length}/4000
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { if (!storySubmitting) setStoryOpen(false); }}
                disabled={storySubmitting}
                data-testid="seller-story-cancel"
                className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={submitStory}
                disabled={storySubmitting}
                data-testid="seller-story-submit"
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
              >
                {storySubmitting
                  ? (isRTL ? 'جاري الإرسال…' : 'Submitting…')
                  : (isRTL ? 'إرسال للمراجعة' : 'Submit for review')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

